import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

const EMAIL_PROVIDERS = ['SendGrid', 'Mailgun', 'AWS SES', 'SMTP', 'Postmark'];

function EmailModal({ tenantId, config, onClose, onSaved }) {
  const isEdit = !!config;
  const [form, setForm] = useState({
    configName: config?.ConfigName || '',
    serviceProvider: config?.ServiceProvider || '',
    apiKey: '',
    fromEmail: config?.FromEmail || '',
    fromName: config?.FromName || '',
    isDefault: config?.IsDefault || false,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.configName || !form.serviceProvider || (!isEdit && !form.apiKey) || !form.fromEmail) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/settings/email/${config.EmailConfigID}`, { tenantId, ...form });
        toast.success('Configuration updated');
      } else {
        await api.post('/settings/email', { tenantId, ...form });
        toast.success('Configuration created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Email Configuration' : 'Add Email Configuration'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Configuration Name</label>
            <input type="text" placeholder="e.g., Primary SendGrid" value={form.configName} onChange={(e) => handleChange('configName', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="required-label">Email Service Provider</label>
            <select value={form.serviceProvider} onChange={(e) => handleChange('serviceProvider', e.target.value)} className="form-select">
              <option value="">Select a provider</option>
              {EMAIL_PROVIDERS.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </div>
          <div className="form-group">
            <label className="required-label">API Key / App Password</label>
            <input type="password" placeholder={isEdit ? 'Leave blank to keep existing' : 'Enter API key'} value={form.apiKey} onChange={(e) => handleChange('apiKey', e.target.value)} />
            {form.serviceProvider && (
              <span className="form-hint">Get API key from {form.serviceProvider} dashboard</span>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="required-label">From Email</label>
              <input type="email" placeholder="noreply@example.com" value={form.fromEmail} onChange={(e) => handleChange('fromEmail', e.target.value)} />
            </div>
            <div className="form-group">
              <label>From Name (Display Name)</label>
              <input type="text" placeholder="Display name" value={form.fromName} onChange={(e) => handleChange('fromName', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label className="toggle-switch">
                <input type="checkbox" checked={form.isDefault} onChange={(e) => handleChange('isDefault', e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
              <span style={{ fontSize: 14 }}>Set as Default Email Configuration</span>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              <span>💾</span> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmailConfig({ tenantId }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editConfig, setEditConfig] = useState(null);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/email/${tenantId}`);
      if (response.data.success) setConfigs(response.data.data);
    } catch {
      toast.error('Failed to load email configurations');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleDelete = async (id) => {
    if (!(await confirmDelete('Email Configuration'))) return;
    try {
      await api.delete(`/settings/email/${id}`);
      toast.success('Configuration deleted');
      loadConfigs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSendTest = async (id) => {
    try {
      const response = await api.post('/settings/email/test', { emailConfigId: id });
      toast.success(response.data.message);
    } catch {
      toast.error('Test failed');
    }
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Email Service Configurations</h3>
        <button className="btn btn-add" onClick={() => { setEditConfig(null); setShowModal(true); }}>
          + Add Configuration
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No email configurations. Click "Add Configuration" to create one.</div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Config Name</th>
              <th>Provider</th>
              <th>From Email</th>
              <th>From Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.map((c) => (
              <tr key={c.EmailConfigID}>
                <td>
                  <span style={{ fontWeight: 500 }}>{c.ConfigName}</span>
                  {c.IsDefault && <span className="badge badge-default">Default</span>}
                </td>
                <td><span className="badge badge-provider">{c.ServiceProvider}</span></td>
                <td>{c.FromEmail}</td>
                <td>{c.FromName || '-'}</td>
                <td>
                  <span className={`badge ${c.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {c.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-send" title="Send Test" onClick={() => handleSendTest(c.EmailConfigID)}>▶</button>
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditConfig(c); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(c.EmailConfigID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <EmailModal
          tenantId={tenantId}
          config={editConfig}
          onClose={() => { setShowModal(false); setEditConfig(null); }}
          onSaved={() => { setShowModal(false); setEditConfig(null); loadConfigs(); }}
        />
      )}
    </>
  );
}

export default EmailConfig;
